/**
 * =============================================================================
 * Hook: useReferralAffiliatesRealtime
 * Versão: 1.0.0
 *
 * Descrição:
 *   Subscreve eventos Postgres Changes nas tabelas `affiliates` e
 *   `affiliate_links` e invalida a query
 *   `['referral-affiliates-paginated', tenantId]` para que a listagem de
 *   afiliados (e a coluna "Link") atualize automaticamente após cadastros
 *   novos, sem necessidade de F5.
 *
 *   Segue exatamente o padrão de `useMyRecentActivitiesRealtime`.
 * =============================================================================
 */
import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';

// Logger dedicado
const logger = createLogger('ReferralAffiliatesRealtime');

/**
 * Hook que mantém a listagem de afiliados sincronizada via Realtime.
 *
 * @param tenantId - ID do tenant ativo (null = não subscreve)
 */
export function useReferralAffiliatesRealtime(tenantId: string | null | undefined) {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!tenantId) return;

    logger.debug('Iniciando Realtime de afiliados', { tenantId });

    // Helper de invalidação — força refetch da query paginada
    const invalidate = (source: string, eventType: string) => {
      logger.debug('Realtime evento recebido — invalidando cache', {
        source,
        eventType,
      });
      queryClient.invalidateQueries({
        queryKey: ['referral-affiliates-paginated', tenantId],
      });
    };

    const channel = supabase
      .channel(`referral-affiliates-rt-${tenantId}`)
      // Cadastro / atualização de afiliados (inclui personal_slug gerado tardiamente)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'affiliates',
          filter: `tenant_id=eq.${tenantId}`,
        },
        (payload) => invalidate('affiliates', payload.eventType),
      )
      // Provisionamento lazy de links de divulgação (aparece na coluna "Link")
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'affiliate_links',
          filter: `tenant_id=eq.${tenantId}`,
        },
        (payload) => invalidate('affiliate_links', payload.eventType),
      )
      .subscribe((status) => {
        logger.debug('Realtime subscription status:', status);
      });

    return () => {
      logger.debug('Removendo Realtime de afiliados', { tenantId });
      supabase.removeChannel(channel);
    };
  }, [tenantId, queryClient]);
}
